import { useEffect, useMemo, useState } from "react";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/contexts/AuthContext";
import { formatDateTime } from "@/lib/utils";
import { Gift, Search, RefreshCw, AlertTriangle, Check, Ban } from "lucide-react";

type StatusFilter = "ALL" | "INVITED" | "TRIAL" | "CONVERTED";

type ReferralRow = {
  id: string;
  referrer: { companyId: string; name: string };
  referee: { companyId: string | null; name: string | null };
  status: "INVITED" | "TRIAL" | "CONVERTED";
  convertedAt: string | Date | null;
  rewardAppliedAt: string | Date | null;
  rewarded: boolean;
  createdAt: string | Date;
};

const STATUS_TABS: { key: StatusFilter; label: string }[] = [
  { key: "ALL", label: "Tous" },
  { key: "INVITED", label: "Invités" },
  { key: "TRIAL", label: "En essai" },
  { key: "CONVERTED", label: "Convertis" },
];

const STATUS_BADGE: Record<ReferralRow["status"], { variant: "secondary" | "warning" | "success"; label: string }> = {
  INVITED: { variant: "secondary", label: "Invité" },
  TRIAL: { variant: "warning", label: "En essai" },
  CONVERTED: { variant: "success", label: "Converti" },
};

export default function Referrals() {
  const { user } = useAuth();
  const canOverride = user?.staffRole === "SUPER_ADMIN" || user?.staffRole === "ADMIN_OPS";

  const [status, setStatus] = useState<StatusFilter>("ALL");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");

  // Debounce the company search.
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  const {
    data,
    isLoading,
    isError,
    error,
    refetch,
    isRefetching,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = trpc.admin.referrals.list.useInfiniteQuery(
    {
      status: status === "ALL" ? undefined : status,
      search: search || undefined,
      limit: 50,
    },
    {
      getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    },
  );

  const rows = useMemo<ReferralRow[]>(
    () => (data?.pages.flatMap((p) => p.items) ?? []) as ReferralRow[],
    [data],
  );

  // Override dialog state.
  const [target, setTarget] = useState<ReferralRow | null>(null);
  const [reason, setReason] = useState("");
  const action: "APPLY" | "CANCEL" = target?.rewarded ? "CANCEL" : "APPLY";

  const utils = trpc.useUtils();
  const override = trpc.admin.referrals.overrideReward.useMutation({
    onSuccess: (res) => {
      setTarget(null);
      setReason("");
      utils.admin.referrals.list.invalidate();
      toast.success(
        res.action === "APPLY" ? "Récompense appliquée" : "Récompense annulée",
      );
    },
    onError: (err) => toast.error(err.message),
  });

  const closeDialog = () => {
    if (override.isPending) return;
    setTarget(null);
    setReason("");
  };

  return (
    <AdminLayout>
      <div className="space-y-6 animate-fade-in">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight flex items-center gap-2">
            <Gift className="h-7 w-7" />
            Parrainages
          </h1>
          <p className="text-muted-foreground">
            Supervision OPS du programme de parrainage BTP — statut, récompense, override manuel
          </p>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex gap-1 border-b">
            {STATUS_TABS.map(({ key, label }) => (
              <button
                key={key}
                onClick={() => setStatus(key)}
                className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                  status === key
                    ? "border-primary text-primary"
                    : "border-transparent text-muted-foreground hover:text-foreground"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Rechercher une entreprise..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="pl-9 w-64"
              />
            </div>
            <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isRefetching}>
              <RefreshCw className={`h-4 w-4 mr-2 ${isRefetching ? "animate-spin" : ""}`} />
              Actualiser
            </Button>
          </div>
        </div>

        <Card>
          <CardContent className="p-0">
            {isLoading ? (
              <div className="p-6 space-y-3">
                {Array.from({ length: 8 }).map((_, i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            ) : isError ? (
              <div className="p-6 flex flex-col items-center gap-3 text-center">
                <AlertTriangle className="h-8 w-8 text-destructive" />
                <div className="text-sm text-muted-foreground">
                  Impossible de charger les parrainages.
                  {error?.message ? ` (${error.message})` : ""}
                </div>
                <Button variant="outline" size="sm" onClick={() => refetch()}>
                  Réessayer
                </Button>
              </div>
            ) : rows.length === 0 ? (
              <div className="p-10 text-center text-muted-foreground text-sm">
                Aucun parrainage{status !== "ALL" || search ? " pour ces filtres" : ""}.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="text-left p-3 font-medium text-muted-foreground">Parrain</th>
                      <th className="text-left p-3 font-medium text-muted-foreground">Filleul</th>
                      <th className="text-left p-3 font-medium text-muted-foreground">Statut</th>
                      <th className="text-left p-3 font-medium text-muted-foreground">Récompense</th>
                      <th className="text-left p-3 font-medium text-muted-foreground">Créé le</th>
                      {canOverride && (
                        <th className="text-right p-3 font-medium text-muted-foreground">Action</th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => {
                      const badge = STATUS_BADGE[r.status];
                      return (
                        <tr key={r.id} className="border-b last:border-0 hover:bg-muted/30 transition-colors">
                          <td className="p-3">
                            <div className="font-medium">{r.referrer.name || "—"}</div>
                            <div className="text-xs text-muted-foreground font-mono">
                              {r.referrer.companyId.slice(0, 8)}…
                            </div>
                          </td>
                          <td className="p-3">
                            {r.status === "CONVERTED" && r.referee.name ? (
                              <>
                                <div className="font-medium">{r.referee.name}</div>
                                <div className="text-xs text-muted-foreground font-mono">
                                  {r.referee.companyId?.slice(0, 8)}…
                                </div>
                              </>
                            ) : (
                              <span className="text-muted-foreground italic">Masqué</span>
                            )}
                          </td>
                          <td className="p-3">
                            <Badge variant={badge.variant}>{badge.label}</Badge>
                          </td>
                          <td className="p-3">
                            {r.rewarded ? (
                              <div className="flex flex-col">
                                <Badge variant="success" className="w-fit">
                                  Appliquée
                                </Badge>
                                <span className="text-xs text-muted-foreground mt-0.5">
                                  {formatDateTime(r.rewardAppliedAt)}
                                </span>
                              </div>
                            ) : (
                              <Badge variant="secondary">Non</Badge>
                            )}
                          </td>
                          <td className="p-3 text-muted-foreground">{formatDateTime(r.createdAt)}</td>
                          {canOverride && (
                            <td className="p-3 text-right">
                              <Button
                                variant={r.rewarded ? "outline" : "default"}
                                size="sm"
                                onClick={() => {
                                  setTarget(r);
                                  setReason("");
                                }}
                              >
                                {r.rewarded ? (
                                  <>
                                    <Ban className="h-4 w-4 mr-1.5" />
                                    Annuler
                                  </>
                                ) : (
                                  <>
                                    <Check className="h-4 w-4 mr-1.5" />
                                    Appliquer
                                  </>
                                )}
                              </Button>
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {hasNextPage && (
          <div className="flex justify-center">
            <Button
              variant="outline"
              onClick={() => fetchNextPage()}
              disabled={isFetchingNextPage}
            >
              {isFetchingNextPage ? "Chargement..." : "Charger plus"}
            </Button>
          </div>
        )}
      </div>

      {/* Override dialog */}
      <Dialog open={!!target} onOpenChange={(open) => (!open ? closeDialog() : undefined)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {action === "APPLY" ? "Appliquer la récompense" : "Annuler la récompense"}
            </DialogTitle>
            <DialogDescription>
              {action === "APPLY" ? (
                <>
                  Créditer manuellement ~1 mois d'abonnement au parrain{" "}
                  <strong>{target?.referrer.name}</strong> (récupération bug Stripe). L'action est
                  tracée dans l'audit log.
                </>
              ) : (
                <>
                  Annuler le crédit de récompense appliqué au parrain{" "}
                  <strong>{target?.referrer.name}</strong>. L'action est tracée dans l'audit log.
                </>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2">
            <label className="text-sm font-medium" htmlFor="override-reason">
              Motif <span className="text-destructive">*</span>
            </label>
            <textarea
              id="override-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              maxLength={1000}
              rows={3}
              placeholder="Ex : crédit Stripe non reçu suite à incident webhook le 12/06"
              className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            />
            {override.isError && (
              <p className="text-xs text-destructive">{override.error?.message}</p>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={closeDialog} disabled={override.isPending}>
              Annuler
            </Button>
            <Button
              variant={action === "CANCEL" ? "destructive" : "default"}
              disabled={override.isPending || reason.trim().length === 0}
              onClick={() => {
                if (!target) return;
                override.mutate({ referralId: target.id, action, reason: reason.trim() });
              }}
            >
              {override.isPending
                ? "En cours..."
                : action === "APPLY"
                  ? "Confirmer l'application"
                  : "Confirmer l'annulation"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
